import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import HowItWorksButton from '../components/HowItWorksButton';
import Sidebar from '../components/Sidebar';
import { OttoPageBar, Otto, OttoSleeping, OttoLoading } from '../components/otto';
import { computeDocumentState, formatDateBritish, DOCUMENT_STATES, docLabelFor } from '../lib/compliance';
import { outstandingLabel, rejectedLabel, expiredLabel, neverDoneLabel } from '../lib/complianceActions';
import { applyLiveDocumentFilter } from '../lib/liveDocuments';
import { computeNeverSent } from '../lib/neverSentDocs';
import { fetchLastChasedMap, chaseCooldownRemaining, formatLastChased, chaseSupplier } from '../lib/chaseCompliance';
import AcknowledgementReviewModal from '../components/compliance/AcknowledgementReviewModal';
import ComplianceOverview from '../components/ComplianceOverview';
import { fmtDateTime } from '../lib/dates';

// Admin-side compliance review queue. Lists every supplier-uploaded
// document grouped by where it sits in the review lifecycle, with
// search/filter/sort. Each row links to the dedicated review page
// at /compliance/:docId/review where the admin can Approve / Request
// info / Reject.
//
// Pills grouped by whose move it is (compliance-queue §2):
//   Needs you          — awaiting_review, expired (never_sent lands in slice B)
//   With the supplier  — needs_clarification, rejected (awaiting a replacement)
//   Watch              — expiring_soon (reminders are automatic)
//   History            — approved + rejected decisions, last 90 days, merged

// Top-level sections on the Compliance page. The review queue (the original
// surface) plus the document/config/overview tools that used to live on the
// Admin Panel + Suppliers page, consolidated here under one Compliance roof.
// Operational compliance — the review queue + the at-a-glance overview. The
// configure-once tools (Fusion Documents + Risk Flag Configuration) moved to
// the Documents page (/documents) in the System group.
const SECTIONS = [
  { key: 'queue',     label: 'Review Queue' },
  { key: 'overview',  label: 'Compliance Overview' },
];

// Five-state document colours match the supplier side (build #13):
// Awaiting Review = teal, Expiring Soon / Needs Clarification = amber
// (retained compliance amber), Approved = green.
//
// Expired is a DEEP RED (#8E1B06), darker than Outstanding's #E0290A rather
// than a different hue. It was near-black, which read as DISABLED — the one
// thing it isn't. Expired is arguably the more serious of the two: cover has
// actually lapsed, where outstanding may only mean it never arrived. It must
// not read as quieter than the state below it.
// Queue pills grouped by WHOSE MOVE IT IS (compliance-queue §2) — the same
// mental model as "Waiting on you" on the briefs page, so the two teach one
// idea. Only the first group is a to-do list; the rest are the supplier's
// court, a watchlist, and a record. Tone drives the count badge + banner dot.
const PILL_GROUPS = [
  { label: 'Needs you', pills: [
    { key: 'awaiting_review', label: 'Awaiting review', tone: 'red' },
    { key: 'expired',         label: 'Expired',         tone: 'red' },
    // 'Never SUBMITTED' — this counts documents the SUPPLIER has never sent
    // us, and 'Never sent' read as the opposite: that WE never sent it to
    // them. That mattered, because until 2026-08-14 the list genuinely did
    // include documents never published to the supplier, so the misreading
    // was also true. The key stays never_sent; only the wording changes.
    { key: 'never_sent',      label: 'Never submitted', tone: 'red' },
  ] },
  { label: 'With the supplier', pills: [
    { key: 'needs_clarification', label: 'Needs clarification', tone: 'grey' },
    { key: 'rejected',            label: 'Rejected',            tone: 'grey' },
  ] },
  { label: 'Watch', pills: [
    { key: 'expiring_soon', label: 'Expiring soon', tone: 'amber' },
  ] },
  { label: 'History', pills: [
    { key: 'history', label: 'History', tone: 'grey' },
  ] },
];
// Needs-you keys in severity order — drives the banner + the default view.
const NEEDS_YOU_KEYS = ['awaiting_review', 'expired', 'never_sent'];

const TONE_HEX = {
  red:   { dot: '#E0290A', bg: 'rgba(224,41,10,0.12)',   fg: '#B71F06' },
  amber: { dot: '#E89A2E', bg: 'rgba(232,154,46,0.15)',  fg: '#9A6206' },
  grey:  { dot: '#9BA08C', bg: 'rgba(155,160,140,0.16)', fg: '#51534A' },
};

const STATE_DOT = {
  [DOCUMENT_STATES.AWAITING_REVIEW]:     '#2E8FA1',
  [DOCUMENT_STATES.NEEDS_CLARIFICATION]: '#E89A2E',
  [DOCUMENT_STATES.EXPIRING_SOON]:       '#E89A2E',
  [DOCUMENT_STATES.EXPIRED]:             '#8E1B06',
  [DOCUMENT_STATES.APPROVED]:            '#92D400',
  // Outstanding covers never-uploaded AND rejected docs; both are a red state,
  // so the dot must be red — a rejected row showing a near-black dot beside its
  // red "Rejected" text was the reported mismatch (document-detail-page §5c).
  [DOCUMENT_STATES.OUTSTANDING]:         '#E0290A',
};

const HISTORY_DAYS = 90;

// "Uploaded N days ago" / "today" / "yesterday". The brief specifies
// the age tints amber over 3 days old as a soft nudge — applied at
// the consumer.
const formatRelativeUpload = (iso) => {
  if (!iso) return { label: '—', days: null };
  const then = new Date(iso).getTime();
  if (isNaN(then)) return { label: '—', days: null };
  const days = Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  if (days < 1) return { label: 'Uploaded today', days };
  if (days === 1) return { label: 'Uploaded yesterday', days };
  return { label: `Uploaded ${days} days ago`, days };
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
};

// Coarse "how long ago" for the Never sent "required since" line.
const formatSince = (iso) => {
  const d = daysAgo(iso);
  if (d == null) return 'unknown';
  if (d >= 60) return `${Math.round(d / 30)} months`;
  if (d >= 14) return `${Math.round(d / 7)} weeks`;
  if (d <= 1) return 'today';
  return `${d} days`;
};

export default function AdminCompliance() {
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [docs, setDocs] = useState([]);
  const [acks, setAcks] = useState([]); // document_acknowledgements rows
  const [meta, setMeta] = useState({}); // document_key → { label, description, satisfied_by, content_richtext, content_file_url, source }
  const [suppliersById, setSuppliersById] = useState(new Map());
  const [requirements, setRequirements] = useState([]); // risk_document_requirements — for "Never sent"
  const [loading, setLoading] = useState(true);
  // Top-level section (Review Queue / Compliance Overview / Fusion Documents /
  // Risk Flag Configuration). activeTab is the review-queue sub-tab.
  const [section, setSection] = useState('queue');
  const [activeTab, setActiveTab] = useState('awaiting_review');
  // Tabs whose question is "which SUPPLIER needs attention" rather than "which
  // document" get grouped. The others are genuinely one row per document.
  const groupedTabs = useMemo(() => new Set(['never_sent', 'awaiting_review']), []);
  // Supplier ids whose group is collapsed. Collapsed rather than expanded is the
  // exception, so the default (empty set) shows everything — someone opening
  // this page should see the work, not a list of names to click.
  const [collapsedSuppliers, setCollapsedSuppliers] = useState(new Set());
  const toggleSupplierCollapsed = useCallback((supplierId) => {
    setCollapsedSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) next.delete(supplierId); else next.add(supplierId);
      return next;
    });
  }, []);
  const collapseAllSuppliers = useCallback((supplierIds) => {
    setCollapsedSuppliers(new Set(supplierIds));
  }, []);
  const expandAllSuppliers = useCallback(() => setCollapsedSuppliers(new Set()), []);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [sortNewestFirst, setSortNewestFirst] = useState(false);
  // Acknowledgement review modal — { ack, docMeta, supplierName } when
  // open, null when closed. Opened by clicking an ack row in the
  // queue; closed by Approve / Reject / Cancel.
  const [reviewingAck, setReviewingAck] = useState(null);
  // §5 chase — last-chased per supplier (rate-limit + display) + in-flight flag.
  const [lastChasedMap, setLastChasedMap] = useState(new Map());
  const [chasing, setChasing] = useState(false);

  const refetch = useCallback(async () => {
    const [docsRes, acksRes, fdRes, supRes, reqRes] = await Promise.all([
      supabase
        .from('supplier_onboarding_documents')
        // Archived documents are out of the working queue (detail-page §5) —
        // they keep their file and history, but nobody is being asked to act
        // on them.
        .select('id, supplier_id, document_key, created_at, expiry_date, expiry_approved_at, expiry_approved_by, supplier_uploaded_at, supplier_uploaded_file_url, supplier_uploaded_file_name, signed_at, risk_override_applicable, review_decision, reviewed_at, reviewed_by, review_reason, review_notes, supplier_reply, supplier_reply_at, not_required')
        .is('archived_at', null),
      supabase
        .from('document_acknowledgements')
        // '*' rather than a column list: the review modal needs the drawn
        // signature (signature_url), and naming a column that doesn't exist
        // yet makes PostgREST fail the WHOLE query — which would take the
        // compliance queue down between deploy and migration.
        .select('*'),
      // Live only — is_active defaults to true, so an unpublished draft would
      // otherwise be chased as a real requirement (see lib/liveDocuments).
      applyLiveDocumentFilter(supabase
        .from('fusion_documents')
        .select('compliance_doc_key, document_name, description, satisfied_by, source, content_richtext, content_file_url, content_file_name')
        .not('compliance_doc_key', 'is', null)),
      supabase
        .from('suppliers')
        .select('id, supplier_name, category, status, compliance_status, risk_flag, created_at')
        .eq('status', 'active'),
      supabase
        .from('risk_document_requirements')
        .select('risk_level, document_key'),
    ]);
    const docsData = docsRes.data || [];
    const acksData = acksRes.data || [];
    const metaMap = {};
    for (const fd of (fdRes.data || [])) {
      metaMap[fd.compliance_doc_key] = {
        label: fd.document_name,
        description: fd.description,
        satisfied_by: fd.satisfied_by || 'upload',
        source: fd.source || null,
        content_richtext: fd.content_richtext || null,
        content_file_url: fd.content_file_url || null,
        content_file_name: fd.content_file_name || null,
      };
    }
    const supMap = new Map();
    for (const s of (supRes.data || [])) supMap.set(s.id, s);
    setDocs(docsData);
    setAcks(acksData);
    setMeta(metaMap);
    setSuppliersById(supMap);
    setRequirements(reqRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  // Bucket each row into a tab. The natural per-doc state covers
  // most tabs directly; the recently_approved / recently_rejected
  // tabs derive from `reviewed_at` falling within the last 30 days.
  //
  // Upload rows wrapped as { kind: 'doc', row } and ack rows as
  // { kind: 'ack', row, sortAt } so the queue can render both with
  // type-aware UI. sortAt centralises the timestamp used by the
  // bucket-level sort below — supplier_uploaded_at for docs,
  // acknowledged_at / reviewed_at for acks depending on tab.
  const bucketed = useMemo(() => {
    const buckets = {
      awaiting_review: [],
      never_sent: [],  // required docs never uploaded — not a document state
      needs_clarification: [],
      expiring_soon: [],
      expired: [],
      rejected: [],   // live — rejected and awaiting a replacement (their move)
      history: [],    // merged approved + rejected decisions, last 90 days
    };
    // "Never sent" — required set minus submitted, across all active
    // suppliers, with a 7-day onboarding grace (§4). Not derivable from the
    // document rows, so computed from the requirements matrix + submissions.
    buckets.never_sent = computeNeverSent({
      suppliers: Array.from(suppliersById.values()),
      requirements,
      records: docs,
      acks,
      liveDocKeys: Object.keys(meta),
      docMeta: meta,
      graceDays: 7,
    }).map(n => ({ kind: 'never_sent', row: n, sortAt: n.requiredSince }));
    for (const d of docs) {
      if (d.not_required) continue;
      if (!suppliersById.has(d.supplier_id)) continue; // inactive supplier
      const state = computeDocumentState(d);
      const reviewedDays = daysAgo(d.reviewed_at);
      const approvedDays = daysAgo(d.expiry_approved_at);

      // Live states
      if (state === DOCUMENT_STATES.AWAITING_REVIEW) buckets.awaiting_review.push({ kind: 'doc', row: d, sortAt: d.supplier_uploaded_at });
      if (state === DOCUMENT_STATES.NEEDS_CLARIFICATION) buckets.needs_clarification.push({ kind: 'doc', row: d, sortAt: d.supplier_uploaded_at });
      if (state === DOCUMENT_STATES.EXPIRING_SOON) buckets.expiring_soon.push({ kind: 'doc', row: d, sortAt: d.supplier_uploaded_at });
      if (state === DOCUMENT_STATES.EXPIRED) buckets.expired.push({ kind: 'doc', row: d, sortAt: d.supplier_uploaded_at });

      // Rejected & awaiting a replacement — the supplier owes you a new upload
      // (the state machine maps rejected → outstanding, so it isn't a state).
      if (d.review_decision === 'rejected') {
        buckets.rejected.push({ kind: 'doc', row: d, sortAt: d.reviewed_at || d.supplier_uploaded_at, outcome: 'rejected' });
      }

      // History (merged, rolling 90-day window) — one record of decisions.
      if (state === DOCUMENT_STATES.APPROVED && approvedDays != null && approvedDays <= HISTORY_DAYS) {
        buckets.history.push({ kind: 'doc', row: d, sortAt: d.expiry_approved_at, outcome: 'approved' });
      }
      if (d.review_decision === 'rejected' && reviewedDays != null && reviewedDays <= HISTORY_DAYS) {
        buckets.history.push({ kind: 'doc', row: d, sortAt: d.reviewed_at, outcome: 'rejected' });
      }
    }
    // Acknowledge-type rows. Pending acks → awaiting_review; decided acks →
    // History (90-day window) and, when rejected, the live rejected bucket.
    for (const a of acks) {
      if (!suppliersById.has(a.supplier_id)) continue;
      if (a.review_decision == null) {
        buckets.awaiting_review.push({ kind: 'ack', row: a, sortAt: a.acknowledged_at });
      } else {
        const days = daysAgo(a.reviewed_at);
        if (days != null && days <= HISTORY_DAYS) {
          buckets.history.push({ kind: 'ack', row: a, sortAt: a.reviewed_at, outcome: a.review_decision });
        }
        if (a.review_decision === 'rejected') {
          buckets.rejected.push({ kind: 'ack', row: a, sortAt: a.reviewed_at, outcome: 'rejected' });
        }
      }
    }
    return buckets;
  }, [docs, acks, suppliersById, requirements, meta]);

  // Default to the first non-empty "needs you" pill (§7) rather than always
  // Awaiting Review — landing on an empty queue while documents block
  // suppliers is the most misleading thing the page can do. Runs once after
  // the first load; any manual pill change is respected thereafter.
  const didInitTab = useRef(false);
  useEffect(() => {
    if (loading || didInitTab.current) return;
    didInitTab.current = true;
    const firstNonEmpty = NEEDS_YOU_KEYS.find(k => (bucketed[k] || []).length > 0);
    if (firstNonEmpty) setActiveTab(firstNonEmpty);
  }, [loading, bucketed]);

  // Otto banner segments (§6) — only non-zero, severity-ordered (awaiting
  // review first: a supplier waiting on a decision outranks a lapsed doc).
  const bannerSegments = useMemo(() => {
    const seg = [];
    const aw = (bucketed.awaiting_review || []).length;
    const ex = (bucketed.expired || []).length;
    const ns = (bucketed.never_sent || []).length;
    const es = (bucketed.expiring_soon || []).length;
    if (aw > 0) seg.push({ dot: 'red', title: `${aw} awaiting your review`, body: 'A supplier is waiting on your decision.', ctaLabel: 'Review →', onCta: () => setActiveTab('awaiting_review') });
    if (ex > 0) seg.push({ dot: 'red', title: `${ex} expired`, body: "These suppliers can't be awarded new work until they're replaced.", ctaLabel: 'View →', onCta: () => setActiveTab('expired') });
    if (ns > 0) seg.push({ dot: 'red', title: `${ns} never submitted`, body: 'Published to the supplier, required of them, and nothing sent back — these suppliers are blocked from work.', ctaLabel: 'View →', onCta: () => setActiveTab('never_sent') });
    if (es > 0) seg.push({ dot: 'amber', title: `${es} expiring within 30 days`, body: 'Reminders go out automatically — nothing to do yet.', ctaLabel: 'View →', onCta: () => setActiveTab('expiring_soon') });
    return seg;
  }, [bucketed]);

  // §5 — last time each supplier was chased, for the rate-limit + display.
  useEffect(() => {
    if (loading) return undefined;
    const ids = Array.from(suppliersById.keys());
    if (!ids.length) return undefined;
    let cancelled = false;
    fetchLastChasedMap(ids).then(m => { if (!cancelled) setLastChasedMap(m); });
    return () => { cancelled = true; };
  }, [loading, suppliersById]);

  // Everything a supplier has OUTSTANDING (expired + never-sent + rejected),
  // for the chase email — one list per supplier, never one per document (§5).
  const outstandingBySupplier = useMemo(() => {
    const m = new Map();
    const add = (sid, doc) => { if (!m.has(sid)) m.set(sid, []); m.get(sid).push(doc); };
    // The action has to match how the document is satisfied. A Code of
    // Conduct is an acknowledge doc — telling its supplier it was "not yet
    // uploaded" asked them for a file that never existed. satisfied_by is
    // carried on every doc so the chase list can say what's actually needed.
    const sb = (key) => meta[key]?.satisfied_by;
    for (const it of bucketed.expired || []) {
      const d = it.row;
      add(d.supplier_id, { document_key: d.document_key, satisfied_by: sb(d.document_key), label: docLabelFor(meta[d.document_key]?.label, d.document_key), statusLabel: expiredLabel(sb(d.document_key), d.expiry_date ? formatDateBritish(d.expiry_date) : null) });
    }
    for (const it of bucketed.never_sent || []) {
      const n = it.row;
      add(n.supplier_id, { document_key: n.document_key, satisfied_by: sb(n.document_key), label: docLabelFor(n.label, n.document_key), statusLabel: outstandingLabel(sb(n.document_key)) });
    }
    for (const it of bucketed.rejected || []) {
      const d = it.row;
      if (d.document_key) add(d.supplier_id, { document_key: d.document_key, satisfied_by: sb(d.document_key), label: docLabelFor(meta[d.document_key]?.label, d.document_key), statusLabel: rejectedLabel(sb(d.document_key)) });
    }
    return m;
  }, [bucketed, meta]);

  const markChased = useCallback((ids) => {
    const iso = new Date().toISOString();
    setLastChasedMap(prev => { const next = new Map(prev); for (const id of ids) next.set(id, iso); return next; });
  }, []);

  // Per-row chase — that supplier, that document.
  const chaseRow = useCallback(async (supplierId, doc) => {
    if (chasing) return;
    setChasing(true);
    const sup = suppliersById.get(supplierId);
    const res = await chaseSupplier({ supplierId, supplierName: sup?.supplier_name, documents: [doc], actorId: currentInternalUser?.id, portalOrigin: window.location.origin });
    setChasing(false);
    if (res.skipped === 'no_recipients') { window.alert(`${sup?.supplier_name || 'This supplier'} has no active portal user to email.`); return; }
    markChased([supplierId]);
  }, [chasing, suppliersById, currentInternalUser, markChased]);

  // Whole-supplier chase — everything that supplier owes, in one email. Used
  // by the grouped never-sent view, where chasing per document would have sent
  // the same "here's everything outstanding" email once per missing document.
  // Reuses outstandingBySupplier so the list matches the bulk chase exactly.
  const chaseSupplierRow = useCallback(async (supplierId) => {
    if (chasing) return;
    const docs = outstandingBySupplier.get(supplierId) || [];
    if (!docs.length) return;
    setChasing(true);
    const sup = suppliersById.get(supplierId);
    const res = await chaseSupplier({ supplierId, supplierName: sup?.supplier_name, documents: docs, actorId: currentInternalUser?.id, portalOrigin: window.location.origin });
    setChasing(false);
    if (res.skipped === 'no_recipients') { window.alert(`${sup?.supplier_name || 'This supplier'} has no active portal user to email.`); return; }
    markChased([supplierId]);
  }, [chasing, outstandingBySupplier, suppliersById, currentInternalUser, markChased]);

  // Bulk chase — one email per affected supplier listing everything they have
  // outstanding; skips any chased within the last 7 days.
  const chaseAll = useCallback(async (tabKey) => {
    if (chasing) return;
    const items = bucketed[tabKey] || [];
    const supplierIds = [...new Set(items.map(it => it.row.supplier_id))];
    const now = Date.now();
    const eligible = supplierIds.filter(sid => chaseCooldownRemaining(lastChasedMap.get(sid), now) === 0);
    if (!eligible.length) { window.alert('Every affected supplier was chased within the last 7 days.'); return; }
    setChasing(true);
    let sent = 0, noRecip = 0;
    for (const sid of eligible) {
      const docs = outstandingBySupplier.get(sid) || [];
      if (!docs.length) continue;
      const sup = suppliersById.get(sid);
      const res = await chaseSupplier({ supplierId: sid, supplierName: sup?.supplier_name, documents: docs, actorId: currentInternalUser?.id, portalOrigin: window.location.origin });
      if (res.emailed > 0) sent++; else if (res.skipped === 'no_recipients') noRecip++;
    }
    markChased(eligible);
    setChasing(false);
    const skipped = supplierIds.length - eligible.length;
    window.alert(`Chased ${sent} supplier${sent === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped (chased in the last 7 days)` : ''}${noRecip ? ` · ${noRecip} had no portal contact` : ''}.`);
  }, [chasing, bucketed, lastChasedMap, outstandingBySupplier, suppliersById, currentInternalUser, markChased]);

  const supplierOptions = useMemo(() => {
    const list = Array.from(suppliersById.values()).sort((a, b) =>
      (a.supplier_name || '').localeCompare(b.supplier_name || '')
    );
    return list;
  }, [suppliersById]);

  // document_key → human label, for the Compliance Overview section's doc rows.
  const docLabelMap = useMemo(() => {
    const m = {};
    for (const [k, v] of Object.entries(meta)) m[k] = v.label;
    return m;
  }, [meta]);

  // The visible rows after search + supplier filter + sort. Rows are
  // wrapped objects { kind: 'doc' | 'ack', row, sortAt } so the queue
  // can intermix upload-doc and acknowledge-doc reviews in one list.
  const visibleRows = useMemo(() => {
    const rows = bucketed[activeTab] || [];
    const q = search.trim().toLowerCase();
    let filtered = rows;
    if (q) {
      filtered = filtered.filter(item => {
        const sup = suppliersById.get(item.row.supplier_id);
        const docName = docLabelFor(meta[item.row.document_key]?.label, item.row.document_key);
        return (sup?.supplier_name || '').toLowerCase().includes(q)
            || docName.toLowerCase().includes(q);
      });
    }
    if (supplierFilter !== 'all') {
      filtered = filtered.filter(item => item.row.supplier_id === supplierFilter);
    }
    // History is a decision record — it always reads newest-first regardless of
    // the toggle (document-detail-page brief §5b). Other tabs honour the toggle.
    const newestFirst = activeTab === 'history' ? true : sortNewestFirst;
    const sorted = [...filtered].sort((a, b) => {
      const aT = new Date(a.sortAt || 0).getTime();
      const bT = new Date(b.sortAt || 0).getTime();
      if (aT === bT) {
        const aN = suppliersById.get(a.row.supplier_id)?.supplier_name || '';
        const bN = suppliersById.get(b.row.supplier_id)?.supplier_name || '';
        return aN.localeCompare(bN);
      }
      return newestFirst ? (bT - aT) : (aT - bT);
    });
    return sorted;
  }, [bucketed, activeTab, search, supplierFilter, sortNewestFirst, suppliersById, meta]);

  const handleRowClick = useCallback((item) => {
    if (item.kind === 'ack') {
      const docMeta = meta[item.row.document_key];
      const sup = suppliersById.get(item.row.supplier_id);
      setReviewingAck({ ack: item.row, docMeta, supplierName: sup?.supplier_name });
      return;
    }
    navigate(`/compliance/${item.row.id}/review`);
  }, [navigate, meta, suppliersById]);

  // Admin / super-user gate. The sidebar nav hides this surface for
  // non-admin users, but a direct URL hit needs the page itself to
  // refuse — Finance and any future read-only roles shouldn't see
  // the supplier review queue.
  if (currentInternalUser && !isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentInternalUser} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="font-['Metropolis'] text-[10px] font-bold tracking-[3px] uppercase text-[#7A4E10] mb-3">Restricted</p>
          <p className="font-['Metropolis'] text-lg text-ink mb-6 max-w-md">Compliance is restricted to Admin and Super User accounts.</p>
          <button onClick={() => navigate('/dashboard')} className="font-['Metropolis'] text-xs font-semibold tracking-[2px] uppercase text-fusion-forest border border-[#92D400] rounded-full px-5 py-2 hover:bg-active-green/10 transition-colors">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // One queue row. Extracted so the flat list and the supplier-grouped tabs
  // render EXACTLY the same row rather than each growing its own copy — the
  // duplication that put three different answers on screen for one document.
  const renderQueueItem = (item) => {
                // Never-sent branch — a required doc with no row to review, so
                // it links to the supplier rather than a review page. Chase
                // lands here in slice C.
                if (item.kind === 'never_sent') {
                  const n = item.row;
                  const sup = suppliersById.get(n.supplier_id);
                  const lc = lastChasedMap.get(n.supplier_id);
                  return (
                    <div
                      key={`ns-${n.supplier_id}-${n.document_key}`}
                      className="flex items-center gap-3 border-b border-line last:border-b-0"
                      style={{ backgroundColor: 'rgba(224,41,10,0.03)' }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/suppliers/${n.supplier_id}`)}
                        className="flex-1 min-w-0 text-left flex items-center gap-4 px-4 py-3.5 hover:bg-white/[0.02] transition-colors">
                        <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#E0290A' }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-['Metropolis'] text-sm font-bold text-ink truncate">{sup?.supplier_name || '—'}</div>
                          <div className="font-['Metropolis'] text-xs text-fusion-mid-grey truncate">{n.label}</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 truncate">
                            {neverDoneLabel(meta[n.document_key]?.satisfied_by)}{n.requiredSince ? ` · required since ${formatSince(n.requiredSince)}` : ''}
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-3 pr-4 flex-shrink-0">
                        {lc && <span className="font-['Metropolis'] text-[10px] text-[#9BA08C] whitespace-nowrap hidden sm:inline">{formatLastChased(lc)}</span>}
                        <ChaseButton lastChasedIso={lc} disabled={chasing}
                          onChase={() => chaseRow(n.supplier_id, { document_key: n.document_key, satisfied_by: meta[n.document_key]?.satisfied_by, label: n.label, statusLabel: outstandingLabel(meta[n.document_key]?.satisfied_by) })} />
                      </div>
                    </div>
                  );
                }

                // Acknowledgement-row branch — visually distinct from
                // upload rows so the admin can scan the queue and tell
                // sign-offs from file uploads at a glance.
                if (item.kind === 'ack') {
                  const a = item.row;
                  const sup = suppliersById.get(a.supplier_id);
                  const docMeta = meta[a.document_key] || {};
                  const isPending = a.review_decision == null;
                  const isApproved = a.review_decision === 'approved';
                  const dot = isPending ? '#2E8FA1' : isApproved ? '#92D400' : '#E0290A';
                  const tint = isPending ? 'rgba(46, 143, 161, 0.04)' : 'transparent';
                  const subLine = isPending
                    ? `Acknowledged ${fmtDateTime(a.acknowledged_at)}`
                    : isApproved
                      ? `Approved ${fmtDateTime(a.reviewed_at)}`
                      : `Rejected ${fmtDateTime(a.reviewed_at)}`;
                  return (
                    <button
                      key={`ack-${a.id}`}
                      type="button"
                      onClick={() => handleRowClick(item)}
                      className="w-full text-left flex items-center gap-4 px-4 py-3.5 border-b border-line last:border-b-0 hover:bg-white/[0.02] transition-colors"
                      style={{ backgroundColor: tint }}>
                      <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-['Metropolis'] text-sm font-bold text-ink truncate">{sup?.supplier_name || '—'}</span>
                          <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.18em] uppercase rounded px-1.5 py-0.5 border border-[#DADCCD]/40 text-[#51534A] flex-shrink-0">
                            Acknowledgement
                          </span>
                        </div>
                        <div className="font-['Metropolis'] text-xs text-fusion-mid-grey truncate">
                          {docLabelFor(docMeta.label, a.document_key)}
                        </div>
                        <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 truncate">
                          <span>{subLine}</span>
                          {a.signer_name && <> · Signed by {a.signer_name}</>}
                        </div>
                      </div>
                      <span className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-fusion-forest flex-shrink-0">
                        {isPending ? 'Review →' : 'View →'}
                      </span>
                    </button>
                  );
                }

                // Upload-doc branch — original render path, unchanged.
                const d = item.row;
                const sup = suppliersById.get(d.supplier_id);
                const docMeta = meta[d.document_key] || {};
                const state = computeDocumentState(d);
                const dot = STATE_DOT[state] || '#51534A';
                const upload = formatRelativeUpload(d.supplier_uploaded_at);
                const expiryStr = d.expiry_date ? formatDateBritish(d.expiry_date) : null;
                const fileName = d.supplier_uploaded_file_name || 'Document';
                const ageTone = upload.days != null && upload.days > 3 ? '#E89A2E' : '#51534A';
                const chaseable = activeTab === 'expired';
                const lc = lastChasedMap.get(d.supplier_id);
                const rowInner = (
                  <>
                    <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-['Metropolis'] text-sm font-bold text-ink truncate">
                        {sup?.supplier_name || '—'}
                      </div>
                      <div className="font-['Metropolis'] text-xs text-fusion-mid-grey truncate">
                        {docLabelFor(docMeta.label, d.document_key)}
                      </div>
                      <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 truncate">
                        {item.outcome === 'approved' ? (
                          <span className="text-fusion-forest">Approved {d.expiry_approved_at ? fmtDateTime(d.expiry_approved_at) : ''}</span>
                        ) : item.outcome === 'rejected' ? (
                          <span className="text-[#B71F06]">Rejected {d.reviewed_at ? fmtDateTime(d.reviewed_at) : ''}</span>
                        ) : (
                          <>
                            <span style={{ color: ageTone }}>{upload.label}</span>
                            {expiryStr && <> · Expires {expiryStr}</>}
                            {fileName && <> · {fileName}</>}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                );
                if (chaseable) {
                  return (
                    <div key={d.id} className="flex items-center gap-3 border-b border-line last:border-b-0" style={{ backgroundColor: 'rgba(58,59,51,0.02)' }}>
                      <button
                        type="button"
                        onClick={() => handleRowClick(item)}
                        className="flex-1 min-w-0 text-left flex items-center gap-4 px-4 py-3.5 hover:bg-white/[0.02] transition-colors">
                        {rowInner}
                      </button>
                      <div className="flex items-center gap-3 pr-4 flex-shrink-0">
                        {lc && <span className="font-['Metropolis'] text-[10px] text-[#9BA08C] whitespace-nowrap hidden sm:inline">{formatLastChased(lc)}</span>}
                        <HistoryLink docId={d.id} navigate={navigate} />
                        <ChaseButton lastChasedIso={lc} disabled={chasing}
                          onChase={() => chaseRow(d.supplier_id, { document_key: d.document_key, satisfied_by: docMeta.satisfied_by, label: docLabelFor(docMeta.label, d.document_key), statusLabel: expiredLabel(docMeta.satisfied_by, d.expiry_date ? formatDateBritish(d.expiry_date) : null) })} />
                      </div>
                    </div>
                  );
                }
                return (
                  // Two destinations, so two controls: the row takes you to the
                  // decision, History to the document's full story (§4
                  // cross-linking). A link nested inside the row button would
                  // be unreachable by keyboard and swallow the row's click.
                  <div
                    key={d.id}
                    className="flex items-center gap-3 border-b border-line last:border-b-0"
                    style={{ backgroundColor: state === DOCUMENT_STATES.AWAITING_REVIEW ? 'rgba(46, 143, 161, 0.03)' : 'transparent' }}>
                    <button
                      type="button"
                      onClick={() => handleRowClick(item)}
                      className="flex-1 min-w-0 text-left flex items-center gap-4 px-4 py-3.5 hover:bg-white/[0.02] transition-colors">
                      {rowInner}
                      <span className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-fusion-forest flex-shrink-0">
                        {item.outcome ? 'View →' : 'Review →'}
                      </span>
                    </button>
                    <div className="pr-4 flex-shrink-0">
                      <HistoryLink docId={d.id} navigate={navigate} />
                    </div>
                  </div>
                );
  };

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="flex-1 flex flex-col">
        <div className="pt-8">
          <div className="px-8 mb-6 flex items-start justify-between gap-6">
            <div className="min-w-0">
              <h1 className="fusion-h1 text-fusion-forest">Compliance</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">
                Review supplier-uploaded documents — approve, request more information, or reject.
              </p>
            </div>
            <HowItWorksButton to="/compliance/how-it-works" />
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Primary section tabs — Review Queue / Compliance Overview /
            Fusion Documents / Risk Flag Configuration. */}
        <div className="mx-8 mt-6">
          <div className="flex items-end gap-1 border-b border-line flex-wrap">
            {SECTIONS.map(s => {
              const isActive = section === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSection(s.key)}
                  className={`font-['Metropolis'] text-sm font-semibold px-5 py-3 transition-colors ${
                    isActive
                      ? 'text-fusion-forest border-b-2 border-[#92D400] -mb-px'
                      : 'text-[#51534A] hover:text-fusion-mid-grey border-b-2 border-transparent'
                  }`}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {section === 'queue' && (
        <>
        {/* Otto banner — carries the non-zero "needs you" states so the page
            opens with what's wrong, not whichever pill is selected (§6). */}
        {!loading && bannerSegments.length > 0 && (
          <div className="mx-8 mt-6">
            <OttoPageBar segments={bannerSegments} />
          </div>
        )}

        {/* Pill groups — grouped by whose move it is (§1/§2). Each group is a
            radiogroup; every pill shows its count, zeros dimmed (§8). */}
        <div className="mx-8 mt-6 mb-6 flex flex-wrap items-center gap-x-6 gap-y-3">
          {PILL_GROUPS.map(group => (
            <div key={group.label} role="radiogroup" aria-label={group.label} className="flex items-center gap-2">
              <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#9BA08C] whitespace-nowrap">{group.label}</span>
              <div className="inline-flex items-center gap-1 bg-white border border-line rounded-xl p-1">
                {group.pills.map(p => {
                  const count = (bucketed[p.key] || []).length;
                  const isActive = activeTab === p.key;
                  const tone = TONE_HEX[p.tone];
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => { didInitTab.current = true; setActiveTab(p.key); }}
                      className={`font-['Metropolis'] text-[12px] font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-2 transition-colors ${
                        isActive ? 'bg-[#92D400]/15 text-fusion-forest' : 'text-[#51534A] hover:text-ink'
                      }`}>
                      <span>{p.label}</span>
                      <span
                        aria-label={`${count} ${count === 1 ? 'document' : 'documents'}`}
                        className={`font-['Metropolis'] text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-tight ${count > 0 ? '' : 'opacity-40'}`}
                        style={count > 0 ? { backgroundColor: tone.bg, color: tone.fg } : { backgroundColor: 'rgba(155,160,140,0.16)', color: '#51534A' }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="mx-8 mb-4 flex items-center gap-3 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search supplier or document…"
            className="flex-1 min-w-[200px] bg-white border border-line rounded-md px-3 py-2 font-['Metropolis'] text-sm text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#92D400]" />
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="bg-white border border-line rounded-md px-3 py-2 font-['Metropolis'] text-sm text-ink focus:outline-none focus:border-[#92D400]">
            <option value="all">All suppliers</option>
            {supplierOptions.map(s => (
              <option key={s.id} value={s.id}>{s.supplier_name}</option>
            ))}
          </select>
          {/* Sort toggle — Title Case to match the ComplianceOverview
              <select> control casing on the Suppliers tab. The two
              surfaces show the same dataset so the control labels
              should read identically; the button shape stays distinct
              because it toggles a binary direction rather than
              choosing from a list. */}
          <button
            type="button"
            onClick={() => setSortNewestFirst(s => !s)}
            className="bg-white border border-line rounded-md px-3 py-2 font-['Metropolis'] text-sm text-fusion-mid-grey hover:border-[#DADCCD] hover:text-ink transition-colors">
            {sortNewestFirst ? 'Newest first ↓' : 'Oldest first ↑'}
          </button>
        </div>

        {/* Bulk chase (§5) — Expired + Never sent. One email per affected
            supplier listing everything outstanding; skips recent chases. */}
        {(activeTab === 'expired' || activeTab === 'never_sent') && !loading && (bucketed[activeTab] || []).length > 0 && (() => {
          const supIds = [...new Set((bucketed[activeTab] || []).map(it => it.row.supplier_id))];
          const eligible = supIds.filter(sid => chaseCooldownRemaining(lastChasedMap.get(sid)) === 0).length;
          return (
            <div className="mx-8 mb-4 flex items-center gap-3 flex-wrap bg-white border border-line rounded-xl px-4 py-3">
              <span className="font-['Metropolis'] text-[13px] text-fusion-mid-grey">
                {supIds.length} supplier{supIds.length === 1 ? '' : 's'} affected. Chasing sends each one a list of everything they have outstanding.
              </span>
              <button
                type="button"
                disabled={chasing || eligible === 0}
                onClick={() => chaseAll(activeTab)}
                className={`ml-auto font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.14em] rounded-full px-4 py-2 border transition-colors ${
                  chasing || eligible === 0 ? 'text-[#9BA08C] border-line cursor-not-allowed' : 'text-fusion-forest border-[#92D400] hover:bg-[#92D400]/10'
                }`}>
                {chasing ? 'Chasing…' : eligible === 0 ? 'All chased recently' : `Chase all ${eligible} supplier${eligible === 1 ? '' : 's'}`}
              </button>
            </div>
          );
        })()}

        {/* List */}
        <div className="mx-8 mb-12">
          {loading ? (
            <div className="py-12"><OttoLoading size="large" message="Loading compliance" /></div>
          ) : visibleRows.length === 0 ? (
            (() => {
              const filtering = search.trim() !== '' || supplierFilter !== 'all';
              // Awaiting-review empty state (empty-states tweak §1/§2). Otto is
              // AWAKE while anything is outstanding anywhere in compliance
              // (awaiting / expired / never-sent) — he only sleeps when there's
              // genuinely nothing to do. Three elements, not one italic run-on.
              if (activeTab === 'awaiting_review' && !filtering) {
                const ex = (bucketed.expired || []).length;
                const ns = (bucketed.never_sent || []).length;
                if (ex + ns === 0) {
                  // State B — nothing outstanding anywhere: sleeping Otto.
                  return (
                    <div className="bg-white border border-line rounded-xl px-8 py-12 text-center">
                      <div className="flex justify-center mb-4"><OttoSleeping size={132} /></div>
                      <p className="font-['Metropolis'] text-lg font-bold text-ink">All clear. Nice.</p>
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-1 max-w-[46ch] mx-auto">
                        Nothing to review, nothing expired, nothing outstanding. Every supplier's paperwork is in order.
                      </p>
                    </div>
                  );
                }
                // State A — review queue clear but work elsewhere: awake Otto,
                // no "Nice", and the outstanding work as its own red block.
                const parts = [ex > 0 ? `${ex} expired` : null, ns > 0 ? `${ns} never submitted` : null].filter(Boolean).join(', ');
                return (
                  <div className="bg-white border border-line rounded-xl px-8 py-12 text-center">
                    <div className="flex justify-center mb-4"><Otto pose="default" size={132} ariaLabel="Otto" /></div>
                    <p className="font-['Metropolis'] text-lg font-bold text-ink">Nothing waiting for review</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-1 max-w-[46ch] mx-auto">
                      Documents land here the moment a supplier submits one.
                    </p>
                    <div className="inline-flex items-start gap-3 bg-[#E0290A]/[0.06] border border-[#E0290A]/25 rounded-xl px-4 py-3 mt-6 text-left max-w-md">
                      <span className="w-8 h-8 rounded-lg bg-[#E0290A]/12 text-[#B71F06] flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" strokeLinecap="round" /></svg>
                      </span>
                      <p className="font-['Metropolis'] text-[13px] text-[#7A3226] leading-snug">
                        <span className="block font-bold text-[#8E1F0A]">{parts}</span>
                        Those suppliers can't be awarded new work.
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <div className="bg-white border border-line rounded-xl p-10 text-center font-['Metropolis'] text-sm text-[#51534A] italic">
                  {filtering ? 'No documents match your search.' : emptyText(activeTab)}
                </div>
              );
            })()
          ) : groupedTabs.has(activeTab) && visibleRows.length > 0 ? (
            // Grouped by supplier. Flat, these tabs repeated the supplier's
            // name once per document — a supplier owing six produced six
            // near-identical rows, and the list read as a wall rather than as
            // "four suppliers need chasing". Same treatment on never-sent and
            // awaiting-review, because the question is the same on both: which
            // SUPPLIER needs attention.
            //
            // Rows inside a group are the same renderQueueItem the flat list
            // uses, so grouping can't quietly become a second row design.
            <div className="flex flex-col gap-3">
              {(() => {
                const groups = new Map();
                for (const item of visibleRows) {
                  const sid = item.row?.supplier_id;
                  if (!sid) continue;
                  if (!groups.has(sid)) groups.set(sid, []);
                  groups.get(sid).push(item);
                }
                const allIds = [...groups.keys()];
                const allCollapsed = allIds.length > 0 && allIds.every(sid => collapsedSuppliers.has(sid));
                // Most-owed first: the supplier with six outstanding is the one
                // worth opening.
                const ordered = [...groups.entries()].sort((a, b) => {
                  if (b[1].length !== a[1].length) return b[1].length - a[1].length;
                  const an = suppliersById.get(a[0])?.supplier_name || '';
                  const bn = suppliersById.get(b[0])?.supplier_name || '';
                  return an.localeCompare(bn);
                });
                const isNeverSent = activeTab === 'never_sent';
                const groupCards = ordered.map(([supplierId, items]) => {
                  const sup = suppliersById.get(supplierId);
                  const lc = lastChasedMap.get(supplierId);
                  const collapsed = collapsedSuppliers.has(supplierId);
                  const panelId = `grp-${supplierId}`;
                  return (
                    <div key={panelId} className="bg-white border border-line rounded-xl overflow-hidden">
                      <div
                        className="flex items-center gap-2 px-3 py-3 border-b border-line"
                        style={{ backgroundColor: isNeverSent ? 'rgba(224,41,10,0.04)' : 'rgba(46,143,161,0.04)' }}>
                        <button
                          type="button"
                          onClick={() => toggleSupplierCollapsed(supplierId)}
                          aria-expanded={!collapsed}
                          aria-controls={panelId}
                          title={collapsed ? 'Show documents' : 'Hide documents'}
                          className="w-6 h-6 flex items-center justify-center rounded text-[#51534A] hover:text-fusion-forest hover:bg-fusion-forest/[0.06] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest flex-shrink-0">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                            style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms ease-out' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                        <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: isNeverSent ? '#E0290A' : '#2E8FA1' }} />
                        <button
                          type="button"
                          onClick={() => navigate(`/suppliers/${supplierId}`)}
                          className="min-w-0 flex-1 text-left group">
                          <div className="font-['Metropolis'] text-sm font-bold text-ink truncate group-hover:text-fusion-forest transition-colors">{sup?.supplier_name || '—'}</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                            {items.length} document{items.length === 1 ? '' : 's'} {isNeverSent ? 'never submitted' : 'awaiting review'}
                          </div>
                        </button>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {isNeverSent && lc && <span className="font-['Metropolis'] text-[10px] text-[#9BA08C] whitespace-nowrap hidden sm:inline">{formatLastChased(lc)}</span>}
                          {/* Chase everything this supplier owes in one email.
                              Per-document chasing is still available on each row
                              below — that one sends the same full list, so it's
                              for when you're looking at a specific document. */}
                          {isNeverSent && (
                            <ChaseButton lastChasedIso={lc} disabled={chasing}
                              onChase={() => chaseSupplierRow(supplierId)} />
                          )}
                        </div>
                      </div>
                      {!collapsed && (
                        <div id={panelId}>
                          {isNeverSent
                            ? items.map(item => {
                                const n = item.row;
                                const satisfiedBy = meta[n.document_key]?.satisfied_by;
                                return (
                                  <div key={`ns-${supplierId}-${n.document_key}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-line last:border-b-0 pl-9">
                                    <div className="min-w-0 flex-1">
                                      <div className="font-['Metropolis'] text-[13px] text-ink truncate">{n.label}</div>
                                      <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 truncate">
                                        {neverDoneLabel(satisfiedBy)}{n.requiredSince ? ` · required since ${formatSince(n.requiredSince)}` : ''}
                                      </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                      <ChaseButton lastChasedIso={lc} disabled={chasing}
                                        onChase={() => chaseRow(n.supplier_id, {
                                          document_key: n.document_key,
                                          satisfied_by: satisfiedBy,
                                          label: n.label,
                                          statusLabel: outstandingLabel(satisfiedBy),
                                        })} />
                                    </div>
                                  </div>
                                );
                              })
                            : items.map(renderQueueItem)}
                        </div>
                      )}
                    </div>
                  );
                });
                return (
                  <>
                    {/* Collapse-all only earns its place once there's enough to
                        scroll past. */}
                    {allIds.length > 2 && (
                      <div className="flex justify-end -mb-1">
                        <button
                          type="button"
                          onClick={() => (allCollapsed ? expandAllSuppliers() : collapseAllSuppliers(allIds))}
                          className="font-['Metropolis'] text-[11px] font-semibold text-[#2E8FA1] hover:underline rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E8FA1]">
                          {allCollapsed ? 'Expand all' : 'Collapse all'}
                        </button>
                      </div>
                    )}
                    {groupCards}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="bg-white border border-line rounded-xl overflow-hidden">
              {visibleRows.map(renderQueueItem)}
            </div>
          )}
        </div>
        </>
        )}

        {section === 'overview' && (
          <div className="flex-1 overflow-auto px-8 pb-12 mt-6">
            {loading ? (
              <div className="py-12"><OttoLoading size="large" message="Loading compliance" /></div>
            ) : (
              <ComplianceOverview
                suppliers={supplierOptions}
                currentInternalUser={currentInternalUser}
                docLabelMap={docLabelMap}
                navigate={navigate}
                // The tiles now filter the tab's own supplier list rather than
                // navigating away, so the only cross-tab jump left is into the
                // Review Queue — which is where every per-document action lives.
                onGoToQueue={() => setSection('queue')}
              />
            )}
          </div>
        )}

      </div>

      {/* Ack review modal — opens from the queue when admin clicks an
          ack-kind row. On Approve / Reject, the DB update + refetch
          here re-runs the bucketing so the row moves to the correct
          audit tab without a manual reload. */}
      {reviewingAck && (
        <AcknowledgementReviewModal
          open
          ack={reviewingAck.ack}
          docMeta={reviewingAck.docMeta}
          supplierName={reviewingAck.supplierName}
          reviewerId={currentInternalUser?.id || null}
          onClose={() => setReviewingAck(null)}
          onActioned={refetch}
        />
      )}
    </div>
  );
}

// Entry point from the queue into the document's own page (detail-page §4) —
// every upload, decision and message for that one document, on one screen.
// Reviewing and reading the history are different jobs, so they're different
// controls rather than the row guessing which you wanted.
function HistoryLink({ docId, navigate }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); navigate(`/compliance/${docId}/detail`); }}
      title="Every upload, decision and message for this document"
      className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.15em] text-[#2E8FA1] hover:text-active-green bg-transparent border-0 cursor-pointer whitespace-nowrap">
      History →
    </button>
  );
}

// Per-row chase control (§5/§9). Disabled + reasoned while within the 7-day
// cooldown; the reason rides on aria-label so it isn't colour/tooltip-only.
function ChaseButton({ lastChasedIso, disabled, onChase }) {
  const remaining = chaseCooldownRemaining(lastChasedIso);
  const cooling = remaining > 0;
  const days = Math.max(1, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
  const label = cooling ? (formatLastChased(lastChasedIso) || 'Chased') : 'Chase';
  return (
    <button
      type="button"
      disabled={disabled || cooling}
      onClick={(e) => { e.stopPropagation(); onChase(); }}
      aria-label={cooling ? `Chase unavailable — ${label}, available again in ${days} day${days === 1 ? '' : 's'}` : 'Send a compliance chase to this supplier'}
      title={cooling ? `Chased recently — available again in ${days} day${days === 1 ? '' : 's'}` : 'Send a reminder to this supplier'}
      className={`flex-shrink-0 font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] rounded-full px-3 py-1.5 border transition-colors ${
        cooling || disabled ? 'text-[#9BA08C] border-line cursor-not-allowed' : 'text-fusion-forest border-[#92D400]/50 hover:bg-[#92D400]/10'
      }`}>
      {label}
    </button>
  );
}

function emptyText(tab) {
  switch (tab) {
    case 'awaiting_review':    return 'Nothing waiting for review. Documents appear here the moment a supplier submits one.';
    case 'needs_clarification': return 'No documents waiting on a supplier reply.';
    case 'expiring_soon':      return 'No documents expiring in the next 30 days.';
    case 'expired':            return 'No expired documents. Suppliers are keeping up to date.';
    case 'never_sent':         return 'Every required document has been submitted at least once. Nothing outstanding.';
    case 'rejected':           return 'No rejected documents waiting on a replacement.';
    case 'history':            return 'No compliance decisions in the last 90 days.';
    default: return 'Nothing to show.';
  }
}
